/**
 * Offers Resource Client
 * Axios-based API client for offers endpoints
 */

import { ApiClient } from './apiClient';
import { isDraftMode } from '../utils/previewMode';

// Import shared types from postPurchases to avoid duplication
import type {
    CheckoutSessionState,
    OrderSummary,
    OrderSummaryItem,
    VariantOption
} from './postPurchases';

// Re-export types for use in hooks
export type {
    CheckoutSessionState, OrderSummary, OrderSummaryItem, VariantOption
};

/**
 * Currency option with rate, amount, and lock info
 */
export interface OfferCurrencyOption {
    rate: number;
    amount: number;
    lock?: boolean;
    date?: string;
}

/**
 * Product info within an offer
 */
export interface OfferProduct {
    id: string;
    name: string;
    description: string | null;
    storeId?: string;
    externalProductId?: string | null;
    externalCollectionIds?: string[] | null;
    active?: boolean;
    isShippable?: boolean;
    isTaxable?: boolean;
    taxCategory?: string;
    unitLabel?: string;
    accountingCode?: string;
    accountId?: string;
    autoSync?: boolean;
    creditPrice?: number | null;
    createdAt?: string;
    updatedAt?: string;
    storefrontRawData?: any;
}

/**
 * Variant info within an offer
 */
export interface OfferVariant {
    id: string;
    productId?: string;
    default?: boolean;
    name: string;
    description?: string | null;
    sku?: string;
    externalVariantId?: string | null;
    price?: number | null;
    currency?: string | null;
    compareAtPrice?: number | null;
    grams?: number | null;
    imageUrl: string | null;
    active?: boolean;
    accountId?: string | null;
    creditPrice?: number | null;
    createdAt?: string;
    updatedAt?: string;
    product?: OfferProduct | null;
}

/**
 * Price info within an offer line item
 */
export interface OfferLineItemPrice {
    id: string;
    createdAt?: string;
    updatedAt?: string;
    accountId?: string;
    productId?: string;
    variantId: string;
    default?: boolean;
    currencyOptions: Record<string, OfferCurrencyOption>;
    recurring?: boolean;
    billingTiming?: string;
    interval?: string;
    intervalCount?: number;
    rebillWithOrder?: boolean;
    rebillMode?: string | null;
    rebillStepIntervalMs?: number | null;
    isExternalSellingPlan?: boolean;
    isInternalSellingPlan?: boolean;
    shopifySellingPlanId?: string | null;
    creditsPerRebill?: number | null;
    deliverySettings?: Record<string, any>;
    priceSettings?: Record<string, any>;
    variant?: OfferVariant | null;
}

/**
 * Line item within an offer
 */
export interface OfferLineItem {
    id: string;
    offerId?: string;
    quantity: number;
    priceId?: string;
    variantId?: string;
    productId?: string;
    price: OfferLineItemPrice | null;
}

/**
 * Summary item within an offer summary
 */
export interface OfferSummaryItem {
    id: string;
    productId: string;
    variantId: string;
    priceId?: string;
    product: {
        name: string;
        description: string;
    };
    variant: {
        name: string;
        description?: string;
        imageUrl: string | null;
        grams?: number | null;
    };
    sku?: string;
    unitAmount: number;
    quantity: number;
    amount: number;
    adjustedAmount: number;
    currency: string;
    adjustments?: any[];
    recurring?: boolean;
    rebillMode?: string | null;
    interval?: string;
    intervalCount?: number;
    totalBillingCycles?: number;
    unitAmountAfterFirstCycle?: number;
    isExternalSellingPlan?: boolean;
    isInternalSellingPlan?: boolean;
    deliverySettings?: Record<string, any>;
    priceSettings?: Record<string, any>;
    orderLineItemProduct?: {
        name: string;
    };
    orderLineItemVariant?: {
        name: string;
        imageUrl: string | null;
    };
    metadata?: Record<string, any>;
}

/**
 * Offer summary containing items and totals
 */
export interface OfferSummary {
    items: OfferSummaryItem[];
    totalWeight?: number;
    subtotalAmount?: number;
    lineItemsPromotionAmount?: number;
    subtotalAdjustedAmount?: number;
    totalPromotionAmount: number;
    lineItemsTaxAmount?: number;
    totalTaxAmount?: number;
    shippingCost?: number;
    shippingCostIsFree?: boolean;
    totalAmount: number;
    totalAdjustedAmount: number;
    adjustments: {
        type: string;
        description: string;
        amount: number;
    }[];
    currency: string;
    _consolidated?: boolean;
}

/**
 * Offer promotion association
 */
export interface OfferPromotion {
    id: string;
    promotionId: string;
    promotion?: {
        id: string;
        name: string;
        code?: string | null;
        automatic?: boolean;
    };
}

/**
 * Main Offer type
 */
export interface Offer {
    id: string;
    createdAt?: string;
    updatedAt?: string;
    storeId?: string;
    type?: string;
    titleTrans: Record<string, string>;
    expiryDate?: string | null;
    summaries: OfferSummary[];
    offerLineItems?: OfferLineItem[];
    promotions?: OfferPromotion[];
}

export class OffersResource {
    constructor(private apiClient: ApiClient) { }

    /**
     * Get a single offer by ID
     */
    async getOfferById(offerId: string): Promise<Offer> {
        const response = await this.apiClient.get(`/api/v1/offers/${offerId}`);
        return response as Offer;
    }

    /**
     * Get offers for a store
     * @param storeId - Store ID
     * @param offerIds - Optional array of offer IDs to filter by
     */
    async getOffers(storeId: string, offerIds?: string[]): Promise<Offer[]> {
        let url = `/api/v1/stores/${storeId}/offers`;

        // Add ids query parameter if provided (backend filtering for performance)
        if (offerIds && offerIds.length > 0) {
            const idsParam = offerIds.map(id => `ids=${encodeURIComponent(id)}`).join('&');
            url += `?${idsParam}`;
        }

        const response = await this.apiClient.get(url);
        return (response as any).offers || [];
    }

    /**
     * Get offers by IDs (now uses backend filtering for better performance)
     */
    async getOffersByIds(storeId: string, offerIds: string[]): Promise<Offer[]> {
        // Use backend filtering instead of fetching all and filtering client-side
        return await this.getOffers(storeId, offerIds);
    }

    /**
     * Preview an offer with calculated summary (no checkout session creation)
     * @param offerId - Offer ID
     * @param currency - Currency code
     * @param lineItems - Optional line items with variant/quantity selections
     *   - Use lineItemId for precise updates (same product, different variants)
     *   - Use productId for simple updates (all items with this product)
     */
    async previewOffer(
        offerId: string,
        currency: string = '',
        lineItems?: Array<{
            lineItemId?: string;
            productId?: string;
            variantId: string;
            quantity: number
        }>
    ): Promise<any> {
        console.log('📡 [OffersResource] Calling preview API:', {
            offerId,
            currency,
            lineItems,
            endpoint: `/api/v1/offers/${offerId}/preview`,
        });

        const response = await this.apiClient.post(`/api/v1/offers/${offerId}/preview`, {
            offerId,
            currency,
            lineItems,
        });

        console.log('📥 [OffersResource] Preview API response:', response);
        return response;
    }

    /**
     * Preview and pay for an offer with variant selections
     * Combines preview + checkout session creation in one call
     * @param offerId - Offer ID
     * @param currency - Currency code
     * @param lineItems - Line items with variant/quantity selections
     * @param returnUrl - Optional return URL for checkout
     * @param mainOrderId - Optional main order ID (for upsells)
     */
    async payPreviewedOffer(
        offerId: string,
        currency: string = '',
        lineItems?: Array<{
            lineItemId?: string;
            productId?: string;
            variantId: string;
            quantity: number
        }>,
        returnUrl?: string,
        mainOrderId?: string
    ): Promise<{
        preview: any;
        checkout: {
            checkoutUrl: string;
            customerId?: string;
            checkoutSessionId?: string;
        };
    }> {
        console.log('💳 [OffersResource] Calling pay-preview API:', {
            offerId,
            currency,
            lineItems,
            returnUrl,
            mainOrderId,
            endpoint: `/api/v1/offers/${offerId}/pay-preview`,
        });

        const response = await this.apiClient.post(`/api/v1/offers/${offerId}/pay-preview`, {
            offerId,
            currency,
            lineItems,
            returnUrl: returnUrl || (typeof window !== 'undefined' ? window.location.href : ''),
            mainOrderId,
        });

        console.log('📥 [OffersResource] Pay-preview API response:', response);
        return response as any;
    }

    /**
     * Create checkout session from previewed offer WITHOUT paying
     * Used for landing pages to prepare checkout before actual payment
     * @param offerId - Offer ID
     * @param currency - Currency code
     * @param lineItems - Line items with variant/quantity selections
     * @param returnUrl - Optional return URL for checkout
     * @param mainOrderId - Optional main order ID
     */
    async toCheckout(
        offerId: string,
        currency: string = '',
        lineItems?: Array<{
            lineItemId?: string;
            productId?: string;
            variantId: string;
            quantity: number
        }>,
        returnUrl?: string,
        mainOrderId?: string
    ): Promise<{
        checkoutSessionId?: string;
        checkoutToken?: string;
        customerId?: string;
        checkoutUrl: string;
    }> {
        console.log('🛒 [OffersResource] Calling to-checkout API:', {
            offerId,
            currency,
            lineItems,
            returnUrl,
            mainOrderId,
            endpoint: `/api/v1/offers/${offerId}/to-checkout`,
        });

        const response = await this.apiClient.post(`/api/v1/offers/${offerId}/to-checkout`, {
            offerId,
            currency,
            lineItems,
            returnUrl: returnUrl || (typeof window !== 'undefined' ? window.location.href : ''),
            mainOrderId,
        });

        console.log('📥 [OffersResource] To-checkout API response:', response);
        return response as any;
    }

    /**
     * Initialize checkout session for an offer
     * @param offerId - Offer ID (required)
     * @param orderId - Order ID (optional - used for post-purchase upsells)
     * @param customerId - Customer ID (optional)
     */
    async initCheckoutSession(
        offerId: string,
        orderId?: string,
        customerId?: string
    ): Promise<{ checkoutSessionId: string }> {
        // 🎯 Check draft mode from URL, localStorage, or cookie
        const draft = isDraftMode();

        const payload: any = {
            offerId,
            returnUrl: typeof window !== 'undefined' ? window.location.href : '',
            customerId: customerId || '',
            draft, // 🎯 Pass draft mode
        };

        // Only include orderId if provided (used for post-purchase upsells)
        if (orderId) {
            payload.orderId = orderId;
        }

        const response = await this.apiClient.post('/api/v1/checkout/offer/init', payload);
        return { checkoutSessionId: (response as any).checkoutSessionId };
    }

    /**
     * Create checkout session for an offer
     */
    async createCheckoutSession(
        offerId: string,
        returnUrl?: string
    ): Promise<{ checkoutUrl: string }> {
        const response = await this.apiClient.post(`/api/v1/offers/${offerId}/checkout`, {
            returnUrl: returnUrl || (typeof window !== 'undefined' ? window.location.origin : ''),
        });
        return { checkoutUrl: (response as any).checkoutUrl };
    }

    /**
     * Pay with checkout session
     */
    async payWithCheckoutSession(
        checkoutSessionId: string,
        orderId?: string
    ): Promise<void> {
        // 🎯 Check draft mode from URL, localStorage, or cookie
        const draft = isDraftMode();

        await this.apiClient.post(`/api/v1/checkout-sessions/${checkoutSessionId}/pay`, {
            checkoutSessionId,
            draft, // 🎯 Use dynamic draft mode instead of hardcoded false
            returnUrl: typeof window !== 'undefined' ? window.location.href : '',
            metadata: {
                comingFromPostPurchase: true,
                postOrder: orderId,
                upsell: true,
                source: 'offer',
            },
        });
    }

    /**
     * Pay for an offer directly
     */
    async payOffer(offerId: string, orderId?: string): Promise<any> {
        // 🎯 Check draft mode from URL, localStorage, or cookie
        const draft = isDraftMode();

        return this.apiClient.post(`/api/v1/offers/${offerId}/pay`, {
            offerId,
            draft, // 🎯 Use dynamic draft mode instead of hardcoded false
            returnUrl: typeof window !== 'undefined' ? window.location.href : '',
            metadata: orderId ? {
                comingFromPostPurchase: true,
                postOrder: orderId,
                upsell: true,
                source: 'offer',
            } : {
                upsell: true,
                source: 'offer',
            },
        });
    }

    /**
     * Transform offer to checkout session with dynamic variant selection
     * Uses lineItems from the offer to create a new checkout session
     */
    async transformToCheckoutSession(
        offerId: string,
        lineItems: { variantId: string; quantity: number }[],
        mainOrderId: string,
        returnUrl?: string
    ): Promise<{ checkoutUrl: string; checkoutSessionId?: string; customerId?: string }> {
        const response = await this.apiClient.post(`/api/v1/offers/${offerId}/transform-to-checkout`, {
            offerId,
            lineItems,
            mainOrderId,
            returnUrl: returnUrl || (typeof window !== 'undefined' ? window.location.origin : ''),
        });
        return {
            checkoutUrl: (response as any).checkoutUrl,
            checkoutSessionId: (response as any).checkoutSessionId,
            customerId: (response as any).customerId,
        };
    }

    /**
     * Transform offer to checkout session (async mode) ⚡
     * Response time: ~50ms (20-50x faster!)
     * 
     * Returns checkoutToken immediately, background job completes processing.
     * Use getCheckout() to fetch full session data - it auto-waits for completion.
     * 
     * @example
     * // Fast transform
     * const { checkoutToken } = await offers.toCheckoutAsync(offerId, currency, lineItems, returnUrl, mainOrderId);
     * 
     * // Redirect user immediately
     * window.location.href = `/checkout/${checkoutToken}/op`;
     * 
     * // By the time page loads, background processing is usually complete
     */
    async toCheckoutAsync(
        offerId: string,
        currency: string = '',
        lineItems?: Array<{
            lineItemId?: string;
            productId?: string;
            variantId: string;
            quantity: number
        }>,
        returnUrl?: string,
        mainOrderId?: string
    ): Promise<{
        checkoutToken: string;
        customerId: string;
        status: 'processing';
    }> {
        console.log('🛒 [OffersResource] Calling to-checkout-async API:', {
            offerId,
            currency,
            lineItems,
            returnUrl,
            mainOrderId,
            endpoint: `/api/v1/offers/${offerId}/to-checkout-async`,
        });

        const response = await this.apiClient.post(`/api/v1/offers/${offerId}/to-checkout-async`, {
            offerId,
            lineItems: lineItems?.map(item => ({
                variantId: item.variantId,
                quantity: item.quantity,
            })) || [],
            returnUrl: returnUrl || (typeof window !== 'undefined' ? window.location.href : ''),
            mainOrderId: mainOrderId || '',
        });

        console.log('📥 [OffersResource] To-checkout-async API response:', response);
        return response as any;
    }

    /**
     * @deprecated Use transformToCheckoutSession instead
     * Transform offer to checkout session with dynamic variant selection
     */
    async transformToCheckout(
        offerId: string,
        returnUrl?: string
    ): Promise<{ checkoutUrl: string }> {
        const response = await this.apiClient.post(`/api/v1/offers/${offerId}/transform-to-checkout`, {
            returnUrl: returnUrl || (typeof window !== 'undefined' ? window.location.origin : ''),
        });
        return { checkoutUrl: (response as any).checkoutUrl };
    }

    /**
     * Initialize checkout session for an offer with variants (similar to postPurchases)
     */
    async initCheckoutSessionWithVariants(
        offerId: string,
        lineItems: { variantId: string; quantity: number }[],
        returnUrl?: string
    ): Promise<{ checkoutSessionId: string }> {
        const response = await this.apiClient.post(`/api/v1/offers/${offerId}/transform-to-checkout`, {
            offerId,
            lineItems,
            returnUrl: returnUrl || (typeof window !== 'undefined' ? window.location.href : ''),
        });
        return { checkoutSessionId: (response as any).checkoutSessionId };
    }

    /**
     * Get order summary for a checkout session
     */
    async getOrderSummary(
        sessionId: string,
        includeVariantOptions = false
    ): Promise<any> {
        return this.apiClient.post(`/api/v1/checkout-sessions/${sessionId}/order-summary`, {
            includeVariantOptions,
        });
    }

    /**
     * Update line items for a checkout session
     */
    async updateLineItems(
        sessionId: string,
        lineItems: { variantId: string; quantity: number }[]
    ): Promise<any> {
        return this.apiClient.post(`/api/v1/checkout-sessions/${sessionId}/line-items`, {
            lineItems,
        });
    }
}
